import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Button } from "@nexus/ui";

import { OrdersList } from "@/features/marketplace/components/orders-list/OrdersList";
import { OrdersListSkeleton } from "@/features/marketplace/components/orders-list/OrdersListSkeleton";
import type { MarketplaceOrder } from "@/features/marketplace/types/marketplace.types";

interface OpportunitySetOrdersTabProps {
  opportunitySetId: string;
  tenantId: string;
  orders: MarketplaceOrder[];
  highlightOrderId?: string;
  isLoading?: boolean;
  isError?: boolean;
}

export function OpportunitySetOrdersTab({
  opportunitySetId,
  tenantId,
  orders,
  highlightOrderId,
  isLoading = false,
  isError = false,
}: OpportunitySetOrdersTabProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <OrdersListSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("opportunities.orders.errorTitle", "Could not load orders")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t("opportunities.orders.empty", "No marketplace orders are linked to this set yet.")}
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/marketplace" search={{ tab: "in-production" }}>
            {t("opportunities.orders.viewAll", "View all orders in Marketplace")}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      <OrdersList
        orders={orders}
        tenantId={tenantId}
        variant="in-production"
        navigationMode="embedded"
        stateScope={`opportunity-set:${opportunitySetId}`}
        highlightOrderId={highlightOrderId}
      />
    </div>
  );
}
